import React from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
} from "reactstrap";

import {
    FiShield,
    FiLock,
    FiChevronRight,
    FiFileText,
} from "react-icons/fi";

import "./PrivacyPolicyPage.css";
import SEO from "../../components/common/seo/SEO";


const PrivacyPolicyPage = () => {

    const sections = [
        {
            id: "information-we-collect",
            title: "Information We Collect",
        },
        {
            id: "how-we-use-share",
            title: "How We Use and Share Information",
        },
        {
            id: "how-we-protect",
            title: "How We Protect Information",
        },
        {
            id: "your-rights",
            title: "Your Rights",
        },
        {
            id: "third-party-links",
            title: "Links to Other Websites",
        },
        {
            id: "changes",
            title: "Changes to Our Privacy Policy",
        },
    ];


    return (

        <>
        

         <SEO
                title="Privacy Policy | FeesWorth"

                description="Read the FeesWorth Privacy Policy to understand how we collect, use, protect, and manage personal and non-personal information when you use our website and services."

                keywords="FeesWorth Privacy Policy, FeesWorth privacy policy, privacy policy, data protection, personal information, information security, website privacy"

                canonical="https://feesworth.com/privacy-policy"
            />

        <main className="privacy-policy-page">

            {/* =========================================
                HERO
            ========================================= */}

            <section className="privacy-policy-hero">

                <Container>

                    <div className="privacy-policy-hero-content">

                        <div className="privacy-policy-icon">
                            <FiShield />
                        </div>

                        <span className="privacy-policy-eyebrow">
                            FEESWORTH
                        </span>

                        <h1>
                            Privacy Policy
                        </h1>

                        <p>
                            Your privacy matters to us. Learn how FeesWorth
                            collects, uses, protects, and manages your
                            information when you use our website and services.
                        </p>

                        <div className="privacy-policy-updated">
                            <FiFileText />
                            <span>
                                Last Updated: 11-Dec-2021
                            </span>
                        </div>

                    </div>

                </Container>

            </section>


            {/* =========================================
                CONTENT
            ========================================= */}

            <section className="privacy-policy-content">

                <Container>

                    <Row className="g-5">

                        {/* =================================
                            SIDEBAR
                        ================================= */}

                        <Col
                            lg="3"
                            className="privacy-policy-sidebar-col"
                        >

                            <div className="privacy-policy-sidebar">

                                <div className="privacy-sidebar-title">
                                    <FiFileText />
                                    <span>
                                        On This Page
                                    </span>
                                </div>

                                <nav>

                                    {sections.map((section, index) => (
                                        <a
                                            key={section.id}
                                            href={`#${section.id}`}
                                        >
                                            <span className="privacy-nav-number">
                                                {String(index + 1).padStart(2, "0")}
                                            </span>

                                            <span>
                                                {section.title}
                                            </span>

                                            <FiChevronRight />
                                        </a>
                                    ))}

                                </nav>

                            </div>

                        </Col>


                        {/* =================================
                            MAIN DOCUMENT
                        ================================= */}

                        <Col lg="9">

                            <Card className="privacy-policy-card">

                                <CardBody>


                                    {/* =================================
                                        INTRODUCTION
                                    ================================= */}

                                    <section className="privacy-policy-section privacy-introduction">

                                        <p>
                                            <strong>FEESWORTH</strong> is
                                            committed to maintaining robust
                                            privacy protections for its users.
                                            Our Privacy Policy (“Privacy
                                            Policy”) is designed to help you
                                            understand how we collect, use and
                                            safeguard the information you
                                            provide to us and to assist you in
                                            making informed decisions when
                                            using our Service.
                                        </p>

                                        <p>
                                            For purposes of this Agreement,
                                            “Site” refers to the FeesWorth’s
                                            website, which can be accessed at
                                            <strong> feesworth.com</strong>.
                                        </p>

                                        <p>
                                            “Service” refers to the WCS’s
                                            services accessed via the Site, in
                                            which users can avail training and
                                            project consultancy. The terms
                                            “we,” “us,” and “our” refer to the
                                            FEESWORTH.
                                        </p>

                                        <p>
                                            “You” refers to you, as a user of
                                            our Site or our Service.
                                        </p>

                                        <div className="privacy-notice-box">

                                            <FiLock />

                                            <p>
                                                By accessing our Site or our
                                                Service, you accept our Privacy
                                                Policy and Terms of Use (found
                                                here: <span>[insert link to Terms of Use]</span>),
                                                and you consent to our
                                                collection, storage, use and
                                                disclosure of your Personal
                                                Information as described in
                                                this Privacy Policy.
                                            </p>

                                        </div>

                                    </section>


                                    {/* =================================
                                        1. INFORMATION WE COLLECT
                                    ================================= */}

                                    <section
                                        id="information-we-collect"
                                        className="privacy-policy-section"
                                    >

                                        <SectionTitle
                                            number="01"
                                            title="Information We Collect"
                                        />

                                        <p>
                                            We collect “Non-Personal
                                            Information” and “Personal
                                            Information.”
                                        </p>

                                        <div className="privacy-definition-grid">

                                            <div className="privacy-definition-card">

                                                <span>
                                                    NON-PERSONAL INFORMATION
                                                </span>

                                                <p>
                                                    Includes information that
                                                    cannot be used to personally
                                                    identify you, such as
                                                    anonymous usage data,
                                                    general demographic
                                                    information we may collect,
                                                    referring/exit pages and
                                                    URLs, platform types,
                                                    preferences you submit and
                                                    preferences that are
                                                    generated based on the data
                                                    you submit and number of
                                                    clicks.
                                                </p>

                                            </div>


                                            <div className="privacy-definition-card">

                                                <span>
                                                    PERSONAL INFORMATION
                                                </span>

                                                <p>
                                                    Includes your email, phone
                                                    number, address, date of
                                                    birth, marital status and
                                                    resume, which you submit to
                                                    us through the registration
                                                    process at the Site.
                                                </p>

                                            </div>

                                        </div>


                                        <h3>
                                            Information collected via
                                            Technology
                                        </h3>

                                        <p>
                                            To activate the Service you do not
                                            need to submit any Personal
                                            Information other than your email
                                            address. To use the Service
                                            thereafter, you do need to submit
                                            further Personal Information,
                                            which may include: list Personal
                                            Information collected.
                                        </p>

                                        <p>
                                            However, in an effort to improve
                                            the quality of the Service, we
                                            track information provided to us
                                            by your browser or by our software
                                            application when you view or use
                                            the Service, such as the website
                                            you came from (known as the
                                            “referring URL”), the type of
                                            browser you use, the device from
                                            which you connected to the Service,
                                            the time and date of access, and
                                            other information that does not
                                            personally identify you.
                                        </p>

                                        <p>
                                            We track this information using
                                            cookies, or small text files which
                                            include an anonymous unique
                                            identifier. Cookies are sent to a
                                            user’s browser from our servers
                                            and are stored on the user’s
                                            computer hard drive.
                                        </p>

                                        <p>
                                            Sending a cookie to a user’s
                                            browser enables us to collect
                                            Non-Personal information about
                                            that user and keep a record of the
                                            user’s preferences when utilizing
                                            our services, both on an individual
                                            and aggregate basis.
                                        </p>


                                        <h3>
                                            Information You Provide Us by
                                            Registering for an Account
                                        </h3>

                                        <p>
                                            In addition to the information
                                            provided automatically by your
                                            browser when you visit the Site,
                                            to become a subscriber to the
                                            Service you will need to create a
                                            personal profile.
                                        </p>

                                        <p>
                                            You can create a profile by
                                            registering with the Service and
                                            entering your email address, and
                                            creating a user name and a
                                            password. By registering, you are
                                            authorizing us to collect, store
                                            and use your email address in
                                            accordance with this Privacy
                                            Policy.
                                        </p>


                                        <h3>
                                            Children’s Privacy
                                        </h3>

                                        <p>
                                            The Site and the Service are not
                                            directed to anyone under the age of
                                            13. The Site does not knowingly
                                            collect or solicit information from
                                            anyone under the age of 13, or
                                            allow anyone under the age of 13 to
                                            sign up for the Service.
                                        </p>

                                        <p>
                                            In the event that we learn that we
                                            have gathered personal information
                                            from anyone under the age of 13
                                            without the consent of a parent or
                                            guardian, we will delete that
                                            information as soon as possible.
                                        </p>

                                    </section>


                                    {/* =================================
                                        2. HOW WE USE AND SHARE
                                    ================================= */}

                                    <section
                                        id="how-we-use-share"
                                        className="privacy-policy-section"
                                    >

                                        <SectionTitle
                                            number="02"
                                            title="How We Use and Share Information"
                                        />

                                        <h3>
                                            Personal Information
                                        </h3>

                                        <p>
                                            Except as otherwise stated in this
                                            Privacy Policy, we do not sell,
                                            trade, rent or otherwise share for
                                            marketing purposes your Personal
                                            Information with third parties
                                            without your consent.
                                        </p>

                                        <p>
                                            We do share Personal Information
                                            with vendors who are performing
                                            services for the Company, such as
                                            the servers for our email
                                            communications who are provided
                                            access to user’s email address for
                                            purposes of sending emails from us.
                                            Those vendors use your Personal
                                            Information only at our direction
                                            and in accordance with our Privacy
                                            Policy.
                                        </p>

                                        <p>
                                            In general, the Personal
                                            Information you provide to us is
                                            used to help us communicate with
                                            you.
                                        </p>

                                        <div className="privacy-example-box">

                                            <span>
                                                EXAMPLES
                                            </span>

                                            <ul>
                                                <li>
                                                    Contact users in response to
                                                    questions
                                                </li>

                                                <li>
                                                    Solicit feedback from users
                                                </li>

                                                <li>
                                                    Provide technical support
                                                </li>

                                                <li>
                                                    Inform users about
                                                    promotional offers
                                                </li>
                                            </ul>

                                        </div>

                                        <p>
                                            We may share Personal Information
                                            with outside parties if we have a
                                            good-faith belief that access, use,
                                            preservation or disclosure of the
                                            information is reasonably necessary
                                            to meet any applicable legal
                                            process or enforceable governmental
                                            request; to enforce applicable
                                            Terms of Service, including
                                            investigation of potential
                                            violations; address fraud, security
                                            or technical concerns; or to protect
                                            against harm to the rights,
                                            property, or safety of our users or
                                            the public as required or permitted
                                            by law.
                                        </p>


                                        <h3>
                                            Non-Personal Information
                                        </h3>

                                        <p>
                                            In general, we use Non-Personal
                                            Information to help us improve the
                                            Service and customize the user
                                            experience.
                                        </p>

                                        <p>
                                            We also aggregate Non-Personal
                                            Information in order to track trends
                                            and analyze use patterns on the
                                            Site. This Privacy Policy does not
                                            limit in any way our use or
                                            disclosure of Non-Personal
                                            Information and we reserve the
                                            right to use and disclose such
                                            Non-Personal Information to our
                                            partners, advertisers and other
                                            third parties at our discretion.
                                        </p>

                                        <p>
                                            In the event we undergo a business
                                            transaction such as a merger,
                                            acquisition by another company, or
                                            sale of all or a portion of our
                                            assets, your Personal Information
                                            may be among the assets transferred.
                                        </p>

                                        <p>
                                            You acknowledge and consent that
                                            such transfers may occur and are
                                            permitted by this Privacy Policy,
                                            and that any acquirer of our assets
                                            may continue to process your
                                            Personal Information as set forth
                                            in this Privacy Policy.
                                        </p>

                                        <p>
                                            If our information practices change
                                            at any time in the future, we will
                                            post the policy changes to the
                                            Site so that you may opt out of the
                                            new information practices.
                                        </p>

                                        <p>
                                            We suggest that you check the Site
                                            periodically if you are concerned
                                            about how your information is used.
                                        </p>

                                    </section>


                                    {/* =================================
                                        3. PROTECTION
                                    ================================= */}

                                    <section
                                        id="how-we-protect"
                                        className="privacy-policy-section"
                                    >

                                        <SectionTitle
                                            number="03"
                                            title="How We Protect Information"
                                        />

                                        <p>
                                            We implement security measures
                                            designed to protect your
                                            information from unauthorized
                                            access.
                                        </p>

                                        <p>
                                            Your account is protected by your
                                            account password and we urge you to
                                            take steps to keep your personal
                                            information safe by not disclosing
                                            your password and by logging out of
                                            your account after each use.
                                        </p>

                                        <p>
                                            We further protect your information
                                            from potential security breaches by
                                            implementing certain technological
                                            security measures including
                                            encryption, firewalls and secure
                                            socket layer technology.
                                        </p>

                                        <div className="privacy-security-box">

                                            <FiShield />

                                            <div>
                                                <strong>
                                                    Security Notice
                                                </strong>

                                                <p>
                                                    However, these measures do
                                                    not guarantee that your
                                                    information will not be
                                                    accessed, disclosed, altered
                                                    or destroyed by breach of
                                                    such firewalls and secure
                                                    server software.
                                                </p>
                                            </div>

                                        </div>

                                        <p>
                                            By using our Service, you
                                            acknowledge that you understand and
                                            agree to assume these risks.
                                        </p>

                                    </section>


                                    {/* =================================
                                        4. RIGHTS
                                    ================================= */}

                                    <section
                                        id="your-rights"
                                        className="privacy-policy-section"
                                    >

                                        <SectionTitle
                                            number="04"
                                            title="Your Rights Regarding the Use of Your Personal Information"
                                        />

                                        <p>
                                            You have the right at any time to
                                            prevent us from contacting you for
                                            marketing purposes.
                                        </p>

                                        <p>
                                            You can also indicate that you do
                                            not wish to receive marketing
                                            communications from us through SMS,
                                            WhatsApp or email.
                                        </p>

                                    </section>


                                    {/* =================================
                                        5. LINKS
                                    ================================= */}

                                    <section
                                        id="third-party-links"
                                        className="privacy-policy-section"
                                    >

                                        <SectionTitle
                                            number="05"
                                            title="Links to Other Websites"
                                        />

                                        <p>
                                            As part of the Service, we may
                                            provide links to or compatibility
                                            with other websites or
                                            applications.
                                        </p>

                                        <p>
                                            However, we are not responsible for
                                            the privacy practices employed by
                                            those websites or the information
                                            or content they contain.
                                        </p>

                                        <p>
                                            This Privacy Policy applies solely
                                            to information collected by us
                                            through the Site and the Service.
                                        </p>

                                        <p>
                                            Therefore, this Privacy Policy does
                                            not apply to your use of a third
                                            party website accessed by selecting
                                            a link on our Site or via our
                                            Service.
                                        </p>

                                        <p>
                                            To the extent that you access or
                                            use the Service through or on
                                            another website or application,
                                            then the privacy policy of that
                                            other website or application will
                                            apply to your access or use of that
                                            site or application.
                                        </p>

                                        <p>
                                            We encourage our users to read the
                                            privacy statements of other websites
                                            before proceeding to use them.
                                        </p>

                                    </section>


                                    {/* =================================
                                        6. CHANGES
                                    ================================= */}

                                    <section
                                        id="changes"
                                        className="privacy-policy-section privacy-policy-last-section"
                                    >

                                        <SectionTitle
                                            number="06"
                                            title="Changes to Our Privacy Policy"
                                        />

                                        <p>
                                            The Company reserves the right to
                                            change this policy and our Terms of
                                            Service at any time.
                                        </p>

                                        <p>
                                            We will notify you of significant
                                            changes to our Privacy Policy by
                                            sending a notice to the primary
                                            email address specified in your
                                            account or by placing a prominent
                                            notice on our site.
                                        </p>

                                        <p>
                                            Significant changes will go into
                                            effect 30 days following such
                                            notification. Non-material changes
                                            or clarifications will take effect
                                            immediately.
                                        </p>

                                        <p>
                                            You should periodically check the
                                            Site and this privacy page for
                                            updates.
                                        </p>


                                        <div className="privacy-final-update">

                                            <FiFileText />

                                            <div>
                                                <span>
                                                    LAST UPDATED
                                                </span>

                                                <strong>
                                                    11-Dec-2021
                                                </strong>
                                            </div>

                                        </div>

                                    </section>

                                </CardBody>

                            </Card>

                        </Col>

                    </Row>

                </Container>

            </section>

        </main>

        </>

    );
};


/* =========================================
   SECTION TITLE
========================================= */

const SectionTitle = ({ number, title }) => {

    return (
        <div className="privacy-section-title">

            <span className="privacy-section-number">
                {number}
            </span>

            <h2>
                {title}
            </h2>

        </div>
    );
};


export default PrivacyPolicyPage;